import { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Upload, type UploadFile, type UploadProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssetService } from '~/app/services/asset/asset.service';
import { SiteService } from '~/app/services/site/site.service';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import {
  KMS_MAX_FILE_BYTES,
  KMS_MAX_FILE_MB,
  buildKmsScope,
  isSupportedKmsFile,
} from '~/app/services/kms/kms-document.utils';
import { KMS_DOCUMENT_TYPE_OPTIONS } from '../utils';
import { kmsUploadFileUid, syncKmsSelectedFilesFromUploadList } from '../utils';
import type {
  KmsBatchUploadProgress,
  KmsScopeKind,
  TrackedUpload,
} from '../types';

export function useKmsUploadPanel(params: {
  organizationId: string | null;
  isSuperAdmin: boolean;
  isPlatformLibrary: boolean;
  presetAssetId?: string;
}) {
  const { organizationId, isSuperAdmin, isPlatformLibrary, presetAssetId } =
    params;
  const { t } = useTranslation('kms');
  const { notification } = App.useApp();

  const [documentType, setDocumentType] = useState<string | undefined>();
  const [scopeKind, setScopeKind] = useState<KmsScopeKind>(
    presetAssetId ? 'asset' : 'global',
  );
  const [siteId, setSiteId] = useState<string | undefined>();
  const [assetId, setAssetId] = useState<string | undefined>(presetAssetId);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [trackedUploads, setTrackedUploads] = useState<TrackedUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [batchProgress, setBatchProgress] =
    useState<KmsBatchUploadProgress | null>(null);

  useEffect(() => {
    if (isPlatformLibrary) {
      setScopeKind('global');
      setSiteId(undefined);
      setAssetId(undefined);
      return;
    }
    if (presetAssetId) {
      setScopeKind('asset');
      setAssetId(presetAssetId);
      setSiteId(undefined);
      return;
    }
    setScopeKind('global');
    setAssetId(undefined);
  }, [isPlatformLibrary, presetAssetId]);

  const sitesQuery = useQuery({
    queryKey: ['kms', 'sites', organizationId],
    queryFn: () => SiteService.findAllByOrganizationId(organizationId!),
    enabled: Boolean(organizationId) && !isPlatformLibrary,
  });

  const assetsQuery = useQuery({
    queryKey: ['kms', 'assets', 'upload', organizationId],
    queryFn: () =>
      AssetService.listByOrganization({
        organizationId: organizationId!,
        page: 1,
        limit: 100,
      }),
    enabled: Boolean(organizationId) && !isPlatformLibrary,
  });

  const documentTypeOptions = useMemo(
    () =>
      KMS_DOCUMENT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t],
  );

  const siteOptions = useMemo(
    () =>
      (sitesQuery.data ?? []).map((site) => ({
        value: site.id,
        label: site.name ?? site.id,
      })),
    [sitesQuery.data],
  );

  const assetOptions = useMemo(
    () =>
      (assetsQuery.data?.data ?? []).map((asset) => ({
        value: asset.id,
        label: asset.assetName || asset.id,
      })),
    [assetsQuery.data],
  );

  const uploadFileList = useMemo<UploadFile[]>(
    () =>
      selectedFiles.map((file) => ({
        uid: kmsUploadFileUid(file),
        name: file.name,
        size: file.size,
        status: 'done',
      })),
    [selectedFiles],
  );

  const scope = useMemo(
    () => buildKmsScope({ kind: scopeKind, siteId, assetId }),
    [assetId, scopeKind, siteId],
  );

  const beforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
    (file) => {
      if (!isSupportedKmsFile(file)) {
        notification.error({
          message: t('errors.invalidFileType.title'),
          description: t('errors.invalidFileType.description'),
        });
        return Upload.LIST_IGNORE;
      }
      if (file.size > KMS_MAX_FILE_BYTES) {
        notification.error({
          message: t('errors.fileTooLarge.title'),
          description: t('errors.fileTooLarge.description', {
            maxSize: KMS_MAX_FILE_MB,
          }),
        });
        return Upload.LIST_IGNORE;
      }

      return false;
    },
    [notification, t],
  );

  const onUploadChange = useCallback<NonNullable<UploadProps['onChange']>>(
    ({ fileList }) => {
      setSelectedFiles((current) =>
        syncKmsSelectedFilesFromUploadList(fileList, current),
      );
    },
    [],
  );

  const clearSelectedFiles = useCallback(() => {
    setSelectedFiles([]);
  }, []);

  const onScopeKindChange = useCallback((next: KmsScopeKind) => {
    setScopeKind(next);
    if (next === 'global') {
      setSiteId(undefined);
      setAssetId(undefined);
    }
    if (next === 'site') {
      setAssetId(undefined);
    }
  }, []);

  const onAssetChange = useCallback((nextAssetId: string | undefined) => {
    setAssetId(nextAssetId);
  }, []);

  const submitUpload = useCallback(async () => {
    if (selectedFiles.length === 0) {
      notification.warning({ message: t('upload.fileRequired') });
      return;
    }
    if (!isPlatformLibrary && !organizationId) {
      notification.warning({ message: t('organizationRequired') });
      return;
    }
    if (scopeKind === 'site' && !siteId) {
      notification.warning({ message: t('upload.siteRequired') });
      return;
    }
    if (scopeKind === 'asset' && !assetId) {
      notification.warning({ message: t('upload.assetRequired') });
      return;
    }

    setIsUploading(true);
    setBatchProgress({ current: 0, total: selectedFiles.length });

    const succeeded: TrackedUpload[] = [];
    const failedFiles: File[] = [];

    try {
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index]!;
        setBatchProgress({ current: index + 1, total: selectedFiles.length });
        try {
          const result = await KmsDocumentService.upload({
            file,
            scope,
            documentType,
            organizationId,
            isSuperAdmin,
          });
          succeeded.push({
            localId: `${result.documentId}-${Date.now()}-${index}`,
            documentId: result.documentId,
            fileName: file.name,
          });
        } catch (error) {
          failedFiles.push(file);
          if (selectedFiles.length === 1) {
            throw error;
          }
        }
      }

      if (succeeded.length > 0) {
        setTrackedUploads((current) => [...succeeded, ...current]);
        setSelectedFiles(failedFiles);
      }

      if (failedFiles.length === 0) {
        notification.success({
          message: t('upload.success.title'),
          description:
            succeeded.length === 1
              ? t('upload.success.description')
              : t('upload.batchSuccess', { count: succeeded.length }),
        });
      } else if (succeeded.length > 0) {
        notification.warning({
          message: t('upload.batchPartialTitle'),
          description: t('upload.batchPartialDescription', {
            success: succeeded.length,
            failed: failedFiles.length,
            files: failedFiles.map((file) => file.name).join(', '),
          }),
        });
      } else {
        notification.error({
          message: t('upload.error.title'),
          description: t('upload.batchAllFailed'),
        });
      }
    } catch (error) {
      notification.error({
        message: t('upload.error.title'),
        description:
          error instanceof Error
            ? error.message
            : t('upload.error.description'),
      });
    } finally {
      setIsUploading(false);
      setBatchProgress(null);
    }
  }, [
    assetId,
    documentType,
    isPlatformLibrary,
    isSuperAdmin,
    notification,
    organizationId,
    scope,
    scopeKind,
    selectedFiles,
    siteId,
    t,
  ]);

  const submitLabel =
    selectedFiles.length <= 1
      ? t('upload.submit')
      : t('upload.submitBatch', { count: selectedFiles.length });

  return {
    documentType,
    setDocumentType,
    documentTypeOptions,
    scopeKind,
    onScopeKindChange,
    siteId,
    setSiteId,
    siteOptions,
    assetId,
    onAssetChange,
    assetOptions,
    selectedFiles,
    uploadFileList,
    beforeUpload,
    onUploadChange,
    clearSelectedFiles,
    submitUpload,
    submitLabel,
    isUploading,
    batchProgress,
    trackedUploads,
    sitesLoading: sitesQuery.isLoading,
    assetsLoading: assetsQuery.isLoading,
    isPlatformLibrary,
  };
}
